// I passi di «Nuova attività» (tela, artboard 41): che attività è, chi la fa e dove, il compito,
// quando parte. Ogni passo legge e scrive la bozza (`draft.ts`); il riepilogo sta nella pagina.
import type {
  AgentProfile,
  ConnectedProvider,
  MemoWatch,
  Pipeline,
  Project,
  RoutineOptions,
  RoutineTrigger,
  ToolGroup,
} from '../../lib/api';
import { Banner } from '../../components/Banner';
import { Field, TextArea, TextField } from '../../components/Field';
import { Pop } from '../../components/Pop';
import { Select } from '../../components/Select';
import { grants } from '../agents/AgentEditor';
import { ModelPicker } from '../agents/ModelPicker';
import { Choice } from './Choice';
import { ConditionEditor } from './RoutineExtras';
import { chainOf, hoursOk, minutesOk, timeOk, workerOf, type Draft } from './draft';

type Set = (patch: Partial<Draft>) => void;

export const TRIGGERS: { id: RoutineTrigger; title: string; hint: string; icon: string }[] = [
  { id: 'manual', title: 'A mano', hint: 'dal pulsante Avvia', icon: 'play' },
  { id: 'input', title: 'Con un input', hint: 'chiede un testo', icon: 'keyboard' },
  { id: 'schedule', title: 'A orario', hint: 'ogni N ore o al giorno', icon: 'clock' },
  { id: 'condition', title: 'Quando succede', hint: 'una condizione', icon: 'zap' },
  { id: 'hook', title: 'Hook', hint: 'da un altro sistema', icon: 'plug-zap' },
];

export const KINDS: { id: RoutineOptions['kind']; title: string; hint: string; icon: string }[] = [
  {
    id: 'text',
    title: 'Attività',
    hint: 'Scegli un pop e lui fa il compito da solo, con i suoi strumenti: ricerche, report, notifiche. Il PM non c’entra.',
    icon: 'clipboard-list',
  },
  {
    id: 'operational',
    title: 'Operativa',
    hint: 'Un pop lavora sui file di un progetto e salva su git, con la prudenza che scegli.',
    icon: 'wrench',
  },
];

export function StepKind({ d, set, pipelines }: { d: Draft; set: Set; pipelines: Pipeline[] }) {
  const operational = d.options.kind === 'operational';
  return (
    <>
      <p className="t-small dim" style={{ margin: 0 }}>
        Scegli prima il tipo: decide cosa chiederemo nei passi dopo.
      </p>
      <div role="radiogroup" aria-label="Che attività è" className="rp-kinds">
        {KINDS.map((k) => (
          <Choice
            key={k.id}
            on={d.options.kind === k.id}
            icon={k.icon}
            title={k.title}
            hint={k.hint}
            onPick={() => set({ options: { ...d.options, kind: k.id } })}
          />
        ))}
      </div>
      <Banner kind="info">
        {operational
          ? 'Un pop lavora sui file di un progetto e salva il lavoro su git, con la prudenza che scegli al passo 2. Esempio: «Bugfixer: prendi l’anomalia più vecchia di memo e correggila».'
          : 'Il PM organizza il lavoro con gli strumenti concessi e consegna un testo nella sessione. Esempi: «Report dello stato dei lavori», «Meteo di Milano», «Riassunto dei ticket aperti».'}
      </Banner>
      {pipelines.length > 1 && (
        <Field label="Filiera" hint="Come PopTown organizza il lavoro: passi, revisioni, limiti.">
          <Select
            label="Filiera"
            value={d.pipeline}
            onChange={(pipeline) => set({ pipeline })}
            options={pipelines.map((p) => ({ value: p.id, label: p.name }))}
            width="100%"
          />
        </Field>
      )}
    </>
  );
}

export function StepWho({
  d,
  set,
  profiles,
  projects,
  providers,
}: {
  d: Draft;
  set: Set;
  profiles: AgentProfile[];
  projects: Project[];
  providers: ConnectedProvider[];
}) {
  const operational = d.options.kind === 'operational';
  const opts = (patch: Partial<RoutineOptions>) => set({ options: { ...d.options, ...patch } });
  const worker = workerOf(d, profiles);
  const chain = chainOf(d);
  return (
    <>
      <Field
        label="Progetto"
        hint={
          operational
            ? 'Obbligatorio: il pop lavora nella sua cartella e nel suo git.'
            : 'Facoltativo: le sessioni finiscono nel progetto e i consumi gli vengono contati.'
        }
      >
        <Select
          label="Progetto"
          value={d.project ?? ''}
          onChange={(v) => set({ project: v || null })}
          options={[
            operational
              ? { value: '', label: 'Scegli il progetto', disabled: true }
              : { value: '', label: 'Nessun progetto' },
            ...projects.map((p) => ({ value: p.id, label: p.name })),
          ]}
          width="100%"
        />
      </Field>
      <Field
        label="Pop"
        hint={
          operational
            ? 'Chi lavora sui file del progetto.'
            : 'Chi fa il compito, senza PM: uno o più pop, nell’ordine in cui li scegli. Ognuno fa la sua parte e passa il lavoro al successivo.'
        }
      >
        {operational ? (
          <div role="radiogroup" aria-label="Pop" className="rp-pops">
            {profiles
              .filter((p) => !p.coordinator)
              .map((p) => {
                const on = p.id === worker?.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    role="radio"
                    aria-checked={on}
                    className="rw-kind rp-pop"
                    data-on={on || undefined}
                    onClick={() => opts({ profile: p.id })}
                  >
                    <Pop avatar={p.avatar} size={44} />
                    <span className="t-strong">{p.label}</span>
                    <span className="t-caption dim">{p.duty}</span>
                  </button>
                );
              })}
          </div>
        ) : (
          // un'Attività: uno o più pop, nell'ordine in cui li scegli (il numero è il turno)
          <div role="group" aria-label="Pop, in ordine" className="rp-pops">
            {profiles
              .filter((p) => !p.coordinator)
              .map((p) => {
                const turn = chain.indexOf(p.id) + 1;
                return (
                  <button
                    key={p.id}
                    type="button"
                    aria-pressed={turn > 0}
                    aria-label={turn > 0 ? `${p.label}, turno ${turn}` : p.label}
                    className="rw-kind rp-pop"
                    data-on={turn > 0 || undefined}
                    onClick={() => {
                      const next = turn > 0 ? chain.filter((x) => x !== p.id) : [...chain, p.id];
                      opts({ profiles: next, profile: next[0] ?? null });
                    }}
                  >
                    {turn > 0 && <span className="rp-turn">{turn}</span>}
                    <Pop avatar={p.avatar} size={44} />
                    <span className="t-strong">{p.label}</span>
                    <span className="t-caption dim">{p.duty}</span>
                  </button>
                );
              })}
          </div>
        )}
      </Field>
      <ModelPicker
        providers={providers}
        value={{
          model: d.options.model ?? null,
          effort: d.options.effort ?? null,
          thinking: d.options.thinking ?? true,
        }}
        onChange={(v) => opts(v)}
        subtitle="prima il provider, poi il modello e quanto ragiona"
        follows={
          worker
            ? {
                choice: { model: worker.model, effort: worker.effort, thinking: worker.thinking },
                resolved: worker.effectiveModel,
              }
            : undefined
        }
        followsName="Quello del pop"
        followsSource={worker ? `la scheda di ${worker.label}` : 'la scheda del pop'}
        followsFrom="dal pop"
      />
      {operational && (
        <Field label="Prudenza con git" hint="Dove arriva il lavoro del pop.">
          <div className="rw-grid">
            <Select
              label="Push"
              value={d.options.push}
              onChange={(v) => opts({ push: v as RoutineOptions['push'] })}
              options={[
                { value: 'project', label: 'Push come il progetto' },
                { value: 'task', label: 'Push dopo ogni lavoro' },
                { value: 'mission', label: 'Push a fine missione' },
                { value: 'manual', label: 'Push solo a mano' },
              ]}
              width="100%"
            />
            <Select
              label="Pull request"
              value={d.options.pullRequest}
              onChange={(v) => opts({ pullRequest: v as RoutineOptions['pullRequest'] })}
              options={[
                { value: 'project', label: 'Pull request come il progetto' },
                { value: 'none', label: 'Nessuna pull request' },
                { value: 'open', label: 'Pull request aperta, senza unire' },
                { value: 'merge', label: 'Pull request aperta e unita' },
              ]}
              width="100%"
            />
          </div>
        </Field>
      )}
    </>
  );
}

export const DELIVERY: {
  id: NonNullable<RoutineOptions['deliver']>;
  title: string;
  hint: string;
}[] = [
  { id: 'session', title: 'Nella sessione', hint: 'La risposta finale del giro, senza file.' },
  { id: 'file', title: 'Anche come file', hint: 'In più un file Markdown nello spazio di lavoro.' },
  { id: 'memo', title: 'Anche su memo', hint: 'In più una nota su memo, nel progetto giusto.' },
];

export function StepTask({ d, set, groups }: { d: Draft; set: Set; groups: ToolGroup[] }) {
  const tools = d.options.tools;
  const flip = (value: string) =>
    set({
      options: {
        ...d.options,
        tools: tools.includes(value) ? tools.filter((t) => t !== value) : [...tools, value],
      },
    });
  return (
    <>
      <Field label="Nome" hint="Come la vedi in Pianifica, es. Report settimanale.">
        <TextField value={d.name} onChange={(name) => set({ name })} autoFocus />
      </Field>
      <Field
        label="Cosa deve fare"
        hint="Scritto come lo chiederesti a una persona: cosa, con quali fonti, in che forma."
      >
        <TextArea value={d.task} onChange={(task) => set({ task })} rows={6} />
      </Field>
      <Field label="Dove consegna il risultato" hint="Il PM lo sa prima di fare il piano.">
        <div role="radiogroup" aria-label="Dove consegna il risultato" className="rp-triggers">
          {DELIVERY.map((x) => (
            <Choice
              key={x.id}
              on={(d.options.deliver ?? 'session') === x.id}
              title={x.title}
              hint={x.hint}
              onPick={() => set({ options: { ...d.options, deliver: x.id } })}
            />
          ))}
        </div>
      </Field>
      <Field
        label="Strumenti concessi"
        hint="Solo questi, oltre a quelli del pop. Le connessioni vengono da Strumenti."
      >
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {grants(groups).map((g) => {
            const on = tools.includes(g.value);
            return (
              <button
                key={g.value}
                type="button"
                aria-pressed={on}
                className="rp-chip"
                data-on={on || undefined}
                onClick={() => flip(g.value)}
              >
                {on ? '✓ ' : ''}
                {g.label}
              </button>
            );
          })}
        </div>
      </Field>
    </>
  );
}

export function StepWhen({
  d,
  set,
  watched,
  groups,
}: {
  d: Draft;
  set: Set;
  watched: MemoWatch[];
  groups: ToolGroup[];
}) {
  return (
    <>
      <div role="radiogroup" aria-label="Quando parte" className="rp-triggers">
        {TRIGGERS.map((t) => (
          <Choice
            key={t.id}
            on={t.id === d.trigger}
            icon={t.icon}
            title={t.title}
            hint={t.hint}
            onPick={() => set({ trigger: t.id })}
          />
        ))}
      </div>
      {d.trigger === 'input' && (
        <Field label="Cosa chiedere all'avvio" hint="Es. «Quale progetto» o «Cosa cercare».">
          <TextField value={d.inputHint} onChange={(inputHint) => set({ inputHint })} />
        </Field>
      )}
      {d.trigger === 'schedule' && (
        <>
          <div className="rw-grid">
            <Field label="Ripeti">
              <Select
                value={d.repeat}
                onChange={(v) => set({ repeat: v as Draft['repeat'] })}
                label="Ripeti"
                options={[
                  { value: 'minutes', label: 'Ogni N minuti' },
                  { value: 'hours', label: 'Ogni N ore' },
                  { value: 'daily', label: 'Ogni giorno alle' },
                ]}
                width="100%"
              />
            </Field>
            {d.repeat === 'minutes' && (
              <Field label="Ogni quanti minuti" error={minutesOk(d) ? undefined : 'Da 1 a 1440'}>
                <TextField
                  value={d.minutes}
                  onChange={(minutes) => set({ minutes })}
                  type="number"
                  invalid={!minutesOk(d)}
                />
              </Field>
            )}
            {d.repeat === 'hours' ? (
              <Field label="Ogni quante ore" error={hoursOk(d) ? undefined : 'Da 1 a 168'}>
                <TextField
                  value={d.hours}
                  onChange={(hours) => set({ hours })}
                  type="number"
                  invalid={!hoursOk(d)}
                />
              </Field>
            ) : (
              <Field label="Alle" error={timeOk(d) ? undefined : 'Scrivi HH:MM'}>
                <TextField
                  value={d.at}
                  onChange={(at) => set({ at })}
                  placeholder="08:00"
                  invalid={!timeOk(d)}
                />
              </Field>
            )}
          </div>
          <div className="t-caption dim">
            Fuso Europe/Rome. Se il giro precedente lavora ancora, quello nuovo si salta.
          </div>
        </>
      )}
      {d.trigger === 'condition' && (
        <ConditionEditor
          value={d.condition}
          onChange={(condition) => set({ condition })}
          watched={watched}
          mailboxes={groups.filter((g) => g.kind === 'gmail')}
          apis={groups.filter((g) => g.kind === 'api')}
        />
      )}
      {d.trigger === 'hook' && (
        <Banner kind="info">
          Dopo il salvataggio vedi indirizzo e token, una volta sola. Il corpo della chiamata
          diventa l’input del giro.
        </Banner>
      )}
    </>
  );
}
